import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  LightBulbIcon,
  DocumentTextIcon,
  UserGroupIcon,
  BookOpenIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";

export function Why1() {
  const { t } = useTranslation();

  const features = [
    {
      title: t(
        "home.why.card1.title",
        "Level Up with personalized on-demand continuing education and mentorship"
      ),
      description: t(
        "home.why.card1.description",
        "Enhance your veterinary professional development. Learn faster at your pace with expert guidance and apply it immediately in clinical practice"
      ),
      icon: LightBulbIcon,
      image: "/images/home/why/ai-insight.webp",
      className: "md:col-span-2 md:row-span-2",
      tag: t("home.why.card1.label", "Grow fast"),
      color: "text-[#00342d]",
    },
    {
      title: t(
        "home.why.card2.title",
        "Instant, evidence-backed answers for your cases"
      ),
      description: t(
        "home.why.card2.description",
        "Get clinical decision support with the latest, referenced veterinary information in seconds to guide your next move with confidence"
      ),
      icon: DocumentTextIcon,
      image: "/images/home/why/report.webp",
      className: "md:col-span-1 md:row-span-1",
      tag: t("home.why.card2.label", "Second opinion in seconds"),
      color: "text-[#00342d]",
    },
    {
      title: t(
        "home.why.card3.title",
        "Instantly generate clean, structured, personalized reports"
      ),
      description: t(
        "home.why.card3.description",
        "Streamline veterinary practice management. Consult as usual, speak your notes with AI transcription, and we write the rest automatically"
      ),
      icon: UserGroupIcon,
      image: "/images/home/why/network.webp",
      className: "md:col-span-1 md:row-span-1",
      tag: t("home.why.card3.label", "Save hours every day"),
      color: "text-[#00342d]",
    },
    {
      title: t(
        "home.why.card4.title",
        "Read only what matters with concise summaries of referred patients"
      ),
      description: t(
        "home.why.card4.description",
        "See the patient’s story in one clear timeline"
      ),
      icon: BookOpenIcon,
      image: "/images/home/why/Library.webp",
      className: "md:col-span-1 md:row-span-1",
      tag: t("home.why.card4.label", "Your private case assistant"),
      color: "text-[#00342d]",
    },
    {
      title: t("home.why.card5.title", "Use our materials and create your own"),
      description: t(
        "home.why.card5.description",
        "Access veterinary templates, clinical protocols, and medical documents anywhere for consistent, evidence-based care across any clinic"
      ),
      icon: ChartBarIcon,
      image: "/images/home/why/summary.webp",
      className: "md:col-span-1 md:row-span-1",
      tag: t("home.why.card5.label", "Build your own library."),
      color: "text-[#00342d]",
    },
    {
      title: t(
        "home.why.card6.title",
        "Connect with the global veterinary community and stay up to date in minutes."
      ),
      description: t(
        "home.why.card6.description",
        "Ask once and get fast, peer-verified answers with real-time updates"
      ),
      icon: UserGroupIcon,
      image: "/images/home/why/vettalk.webp",
      className: "md:col-span-1 md:row-span-1",
      tag: t("home.why.card6.label", "Expand your network"),
      color: "text-[#00342d]",
    },
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#00342d] mb-6 tracking-tight"
            style={{ fontFamily: "'Bruno Ace', sans-serif" }}
          >
            {t("home.why.title", "Why Choose VettConsult?")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-600 leading-relaxed"
          >
            {t(
              "home.why.subtitle",
              "Experience the future of veterinary practice with our cutting-edge features."
            )}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto auto-rows-[minmax(250px,auto)]">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={`group relative overflow-hidden rounded-3xl bg-white border border-gray-100 shadow-sm hover:shadow-xl transition-shadow duration-300 ${feature.className}`}
            >
              <div className="absolute inset-0 z-0">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              </div>

              <div className="relative z-10 h-full flex flex-col justify-end p-6 sm:p-8">
                <div className="flex items-center justify-between mb-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center backdrop-blur-xl bg-white/20 border border-white/30 shadow-lg ${feature.color}`}
                  >
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/20 backdrop-blur-md border border-white/30 text-white">
                    {feature.tag}
                  </span>
                </div>

                <h3 className="text-lg sm:text-xl font-bold text-white mb-2 transition-transform duration-300 group-hover:-translate-y-1">
                  {feature.title}
                </h3>
                <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-300 ease-out">
                  <p className="overflow-hidden text-gray-200 text-sm font-medium max-w-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                    {feature.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Why1;
// Force reload
